import React, { useEffect, useState } from 'react';
import { Star, Quote } from 'lucide-react';

export const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/testimonials`);
        if (!response.ok) {
          throw new Error('Failed to fetch testimonials');
        }
        const data = await response.json();
        setTestimonials(data);
      } catch (err) {
        console.error('Error fetching testimonials:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, [BACKEND_URL]);
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              What Our Clients Say
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Real feedback from 5000+ satisfied customers who experienced our lasting waterproofing solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="bg-slate-800 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-white mb-2">5000+</div>
              <div className="text-slate-300">Satisfied Customers</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white mb-2">2000+</div>
              <div className="text-slate-300">Homes Protected</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white mb-2">26</div>
              <div className="text-slate-300">Years of Trust</div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-slate-600">Loading testimonials...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600">Failed to load testimonials. Please try again later.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="bg-slate-50 rounded-lg p-6 border border-slate-200 relative">
                  <Quote className="w-10 h-10 text-slate-300 mb-4" />
                  <div className="flex items-center mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-4 leading-relaxed">"{testimonial.feedback}"</p>
                  <div className="border-t border-slate-200 pt-4">
                    <p className="font-semibold text-slate-900">{testimonial.name}</p>
                    <p className="text-sm text-slate-600">{testimonial.location}</p>
                    <p className="text-xs text-slate-500 mt-1">{testimonial.project}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Trusted Across Sectors
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Government Satisfaction</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Multiple government infrastructure projects completed with satisfactory completion ratings from public authorities.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Residential Excellence</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                5000+ homeowners trust us for permanent waterproofing solutions. Long-term results speak for themselves.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-800 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Join 5000+ Satisfied Customers
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Experience the difference of professional waterproofing backed by 26 years of expertise.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-slate-800 rounded-lg text-base font-semibold hover:bg-slate-100 transition-colors"
          >
            Get Your Free Consultation
          </a>
        </div>
      </section>
    </div>
  );
};